import { ChevronLeft, ChevronRight, Pencil, ExternalLink} from "lucide-react";
import { useRouter } from "next/navigation";
import { useState, useMemo, useCallback } from "react";

import type { AppointmentStatus } from "@/app/(authenticated)/appointments/_modules/constants/form-options";
import RenderIf from "@/app/_module/components/RenderIf";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { Calendar } from "@/app/_module/components/ui/calendar";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import type { TStatus } from "@/app/_module/types/filter-types";
import { formatTime } from "@/app/_module/utils/formatters";

import { appointmentStatusOptions, formatStatusLabel } from "../constants/form-options";
import { statusColors } from "../constants/statusColors";
import { useUpdateAppointment } from "../hooks/queries";
import type { Appointment } from "../types/appointments";
import { getStatusDisplayName } from "../util/helpers";

interface AppointmentCalendarProps {
  appointments: Appointment[] | undefined;
  onEditAppointment?: (appointment: Appointment) => void;
  /** Bulk-select support for the selected day. Omit to hide the checkboxes. */
  selectedIds?: Set<string>;
  onSelectChange?: (appointmentId: string, selected: boolean) => void;
  onSelectAllForDay?: (appointmentIds: string[], selected: boolean) => void;
}

export const AppointmentCalendar = function ({
  appointments,
  onEditAppointment,
  selectedIds,
  onSelectChange,
  onSelectAllForDay,
}: AppointmentCalendarProps) {
  const router = useRouter();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const { mutate: updateAppointment } = useUpdateAppointment();

  // Helper function to get appointment date (normalized to local date, ignoring time)
  const getAppointmentDate = (appointment: Appointment): Date | null => {
    // Prefer the date field (YYYY-MM-DD string) as it's timezone-agnostic
    // This avoids issues where UTC startTime crosses date boundaries in local timezone
    if (appointment.date) {
      // Parse the date string (YYYY-MM-DD) as local date
      let dateStr: string;
      if (typeof appointment.date === 'string') {
        dateStr = appointment.date;
      } else {
        // Format date in local timezone to avoid UTC shift
        const d = new Date(appointment.date);
        dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed
    }
    // Fall back to startTime if date field doesn't exist
    if (appointment.startTime) {
      const date = new Date(appointment.startTime);
      // Normalize to local date at midnight
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }
    return null;
  };

  // Get appointments for the selected date
  // Note: Not using useMemo here to ensure we always get fresh data after updates
  const selectedDateAppointments = (appointments ?? []).filter((appointment) => {
    const appointmentDate = getAppointmentDate(appointment);
    if (!appointmentDate) return false;

    // Normalize selected date to midnight for comparison
    const normalizedSelectedDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    return appointmentDate.getTime() === normalizedSelectedDate.getTime();
  });

  // Get dates that have appointments - memoized to prevent unnecessary recalculations
  const appointmentDates = useMemo(() => {
    return (appointments ?? [])
      .map(getAppointmentDate)
      .filter((date): date is Date => date !== null);
  }, [appointments]);

  // Helper to get lead ID from appointment (direct field or from associations)
  const getLeadId = (appointment: Appointment): string | undefined => {
    if (appointment.leadId) return appointment.leadId;
    const leadAssoc = appointment.associations?.find(a => a.entityType === "Lead");
    return leadAssoc?.entityId;
  };

  // Helper to get display time for an appointment
  const getDisplayTime = useCallback((appointment: Appointment): string => {
    if (appointment.time) {
      return formatTime(appointment.time);
    }
    if (appointment.startTime) {
      const startTime = new Date(appointment.startTime);
      return formatTime(startTime.toTimeString().slice(0, 5));
    }
    return formatTime("00:00");
  }, []);

  // Helper to get time string for sorting
  const getTimeForSorting = useCallback((appointment: Appointment): string => {
    if (appointment.startTime) {
      const startTime = new Date(appointment.startTime);
      return startTime.toTimeString().slice(0, 5);
    }
    return appointment.time || "00:00";
  }, []);

  // Build the month grid data
  const monthGridData = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = firstDay.getDay(); // 0=Sun
    const daysInMonth = lastDay.getDate();

    // Build a map of date string -> appointments for the current month
    const appointmentsByDate = new Map<string, Appointment[]>();
    for (const appt of (appointments ?? [])) {
      const d = getAppointmentDate(appt);
      if (!d) continue;
      if (d.getMonth() === month && d.getFullYear() === year) {
        const key = `${year}-${month}-${d.getDate()}`;
        const existing = appointmentsByDate.get(key) || [];
        existing.push(appt);
        appointmentsByDate.set(key, existing);
      }
    }

    // Build weeks array
    const weeks: { day: number | null; date: Date | null; appointments: Appointment[] }[][] = [];
    let currentWeek: { day: number | null; date: Date | null; appointments: Appointment[] }[] = [];

    // Leading empty cells
    for (let i = 0; i < startOffset; i++) {
      currentWeek.push({ day: null, date: null, appointments: [] });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const key = `${year}-${month}-${day}`;
      const dayAppointments = (appointmentsByDate.get(key) || []).sort((a, b) => {
        const [ha, ma] = getTimeForSorting(a).split(":").map(Number);
        const [hb, mb] = getTimeForSorting(b).split(":").map(Number);
        return ha * 60 + ma - (hb * 60 + mb);
      });
      currentWeek.push({ day, date, appointments: dayAppointments });
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    // Trailing empty cells
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push({ day: null, date: null, appointments: [] });
      }
      weeks.push(currentWeek);
    }

    return weeks;
  }, [currentMonth, appointments]);

  const today = new Date();
  const isToday = (date: Date | null) => {
    if (!date) return false;
    return date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate();
  };

  const isSelected = (date: Date | null) => {
    if (!date) return false;
    return date.getFullYear() === selectedDate.getFullYear() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getDate() === selectedDate.getDate();
  };

  const goToPrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    const now = new Date();
    setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedDate(now);
  };

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="space-y-6">
      {/* Mini calendar + selected day detail */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Calendar</CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              className="w-full"
              mode="single"
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              onSelect={(date) => date && setSelectedDate(date)}
              selected={selectedDate}
              modifiers={{
                hasAppointment: appointmentDates,
              }}
              modifiersClassNames={{
                hasAppointment: "font-semibold relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:bg-gray-900 after:rounded-full [&:not([data-selected])]:bg-gray-300 dark:[&:not([data-selected])]:bg-gray-700 [&[data-selected]]:ring-2 [&[data-selected]]:ring-gray-900 [&[data-selected]]:ring-offset-2",
              }}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {selectedDate.toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RenderIf condition={!selectedDateAppointments.length}>
              <p className="text-muted-foreground">
                No appointments scheduled for this date.
              </p>
            </RenderIf>

            {!!onSelectAllForDay && selectedDateAppointments.length > 0 && (
              <label className="mb-3 flex w-fit cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  className="h-4 w-4 cursor-pointer accent-primary"
                  checked={
                    selectedDateAppointments.length > 0 &&
                    selectedDateAppointments.every((a) => selectedIds?.has(a.id))
                  }
                  onChange={(e) =>
                    onSelectAllForDay(
                      selectedDateAppointments.map((a) => a.id),
                      e.target.checked,
                    )
                  }
                />
                Select all {selectedDateAppointments.length} on this day
              </label>
            )}

            <div className="space-y-3">
              {selectedDateAppointments
                .sort((a, b) => {
                  const [ha, ma] = getTimeForSorting(a).split(":").map(Number);
                  const [hb, mb] = getTimeForSorting(b).split(":").map(Number);
                  return ha * 60 + ma - (hb * 60 + mb);
                })
                .map((appointment) => (
                  <div
                    key={`${appointment.id}-${appointment.time}-${appointment.updatedAt}`}
                    className={`flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-all ${selectedIds?.has(appointment.id) ? "ring-2 ring-primary bg-primary/5" : ""}`}
                    // Press edits the appointment; the lead has its own button.
                    onClick={() => onEditAppointment?.(appointment)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onEditAppointment?.(appointment);
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      {!!onSelectChange && (
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-primary"
                          checked={!!selectedIds?.has(appointment.id)}
                          aria-label={`Select ${appointment?.title || "appointment"}`}
                          // The row itself navigates to the lead; without this the
                          // checkbox click would open it instead of ticking.
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => onSelectChange(appointment.id, e.target.checked)}
                        />
                      )}
                    <div className="space-y-1">
                      <div className="font-medium">{appointment?.title}</div>
                      <div className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
                        <span>{getDisplayTime(appointment)}</span>
                        {/* Lead name — the row already navigates to the lead on
                            click, but never said whose lead it was. */}
                        {(() => {
                          const leadName = appointment.associations?.find(
                            (a) => a.entityType === "Lead",
                          )?.entityName;
                          const leadId = getLeadId(appointment);
                          return leadId ? (
                            <>
                              <span aria-hidden>·</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  router.push(`/leads/${leadId}`);
                                }}
                                className="inline-flex items-center gap-1 truncate font-medium text-primary hover:underline"
                                title={leadName ? `Open ${leadName}` : "Open lead"}
                              >
                                {leadName || "View lead"}
                                <ExternalLink className="h-3 w-3 shrink-0" />
                              </button>
                            </>
                          ) : null;
                        })()}
                      </div>
                      {(appointment.notes || appointment.description) && (
                        <div className="text-xs text-muted-foreground">
                          {appointment.notes || appointment.description}
                        </div>
                      )}
                    </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditAppointment?.(appointment);
                        }}
                        title="Edit appointment"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Badge className={`${statusColors[appointment.status as TStatus || 'pending']} cursor-pointer hover:opacity-80`}>
                            {getStatusDisplayName(appointment.status as TStatus)}
                          </Badge>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {appointmentStatusOptions.map((status) => (
                            <DropdownMenuItem
                              key={status}
                              onClick={(e) => {
                                e.stopPropagation();
                                updateAppointment({
                                  appointmentId: appointment.id,
                                  data: { status }
                                });
                              }}
                            >
                              {formatStatusLabel(status)}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Full month grid view */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl">
              {currentMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={goToToday}>
                Today
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={goToPrevMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={goToNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 md:p-6 md:pt-0">
          {/* Day headers */}
          <div className="grid grid-cols-7 border-b">
            {dayNames.map((name) => (
              <div key={name} className="px-2 py-2 text-center text-xs font-medium text-muted-foreground uppercase">
                {name}
              </div>
            ))}
          </div>

          {/* Month grid */}
          <div className="grid grid-cols-7">
            {monthGridData.flat().map((cell, idx) => (
              <div
                key={idx}
                className={`min-h-[100px] md:min-h-[120px] border-b border-r p-1 md:p-2 transition-colors cursor-pointer ${
                  cell.day === null
                    ? "bg-muted/30"
                    : isSelected(cell.date)
                      ? "bg-primary/5 ring-1 ring-inset ring-primary"
                      : isToday(cell.date)
                        ? "bg-primary/5"
                        : "hover:bg-muted/50"
                } ${idx % 7 === 0 ? "border-l" : ""}`}
                onClick={() => {
                  if (cell.date) {
                    setSelectedDate(cell.date);
                  }
                }}
              >
                {cell.day !== null && (
                  <>
                    <div className={`text-sm mb-1 ${
                      isToday(cell.date)
                        ? "bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center font-semibold mx-auto md:mx-0"
                        : isSelected(cell.date)
                          ? "font-semibold text-primary"
                          : "text-foreground"
                    }`}>
                      {cell.day}
                    </div>
                    <div className="space-y-0.5 overflow-y-auto max-h-[70px] md:max-h-[90px]">
                      {cell.appointments.map((appt) => (
                        <div
                          key={appt.id}
                          className={`text-[10px] md:text-xs px-1 md:px-1.5 py-0.5 rounded truncate cursor-pointer hover:opacity-80 flex items-center gap-0.5 ${
                            statusColors[appt.status as TStatus || 'pending'] || 'bg-primary/10 text-primary'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const apptLeadId = getLeadId(appt);
                            if (apptLeadId) {
                              router.push(`/leads/${apptLeadId}`);
                            } else {
                              onEditAppointment?.(appt);
                            }
                          }}
                          title={`${getDisplayTime(appt)} - ${appt.title}${getLeadId(appt) ? ' (Click to view lead)' : ''}`}
                        >
                          <span className="truncate">
                            <span className="font-medium">{getDisplayTime(appt)}</span>{" "}
                            <span className="hidden md:inline">{appt.title}</span>
                          </span>
                          <button
                            type="button"
                            className="hidden md:inline-flex shrink-0 hover:opacity-60"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditAppointment?.(appt);
                            }}
                            title="Edit appointment"
                          >
                            <Pencil className="h-2.5 w-2.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
